import React, { useContext, useEffect, useState } from 'react';
import { DashboardContainerColorProps } from 'enums/DashboardContainerEnums';
import CreateProductContainer from 'views/CreateProductContainer';
import { validationSchema } from './ProductDistributionConstants';
import { Formik, FormikProps } from 'formik';
import ProductsService from 'services/ProductsService/products.service';
import { ProductDistributionProps } from 'enums/ProductDistributionEnums';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { AppRoutes } from 'routes/routeConstants/appRoutes';
import FlowContainer from 'views/FlowContainer';
import COAlertModal from 'shared/components/COAlertModal';
import HelpdeskService from 'services/HelpdeskService/Helpdesk.service';
import { HelpDeskContext } from 'context/HelpDeskContext';
import isProductDisabled from 'shared/utils/isProductDisabled';
import { Address } from 'models/Address/address.model';
import FindLocationModal from 'shared/components/FindLocationForDistribution';
import { deserialize } from 'serializr';
import { ProcessingFacilityFieldEnums } from 'enums/ProcessingFacilityEnums';
import { validateForHandleSave } from './validateForHandleSave';
import { ProductDistributionFormData } from 'models/ProductDistributionFormData/ProductDistributionFormData.model';
import ProductDistributionForm from './ProductDistributionForm';
import Stage1AndStage2ProductService from 'services/ProductsService/stage1AndStage2Product.service';
import ProductCreationService from 'services/ProductsService/productCreation.service';

const ProductDistribution = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const productId = Number(id);

  if (!productId) {
    navigate(AppRoutes.DASHBOARD);
  }
  const { setHelpDeskData } = useContext(HelpDeskContext);

  const { fetchData, fetchedHelpDeskData } = HelpdeskService();

  const [selectedIndex, setSelectedIndex] = useState<number>();

  useEffect(() => {
    fetchedHelpDeskData && setHelpDeskData(fetchedHelpDeskData);
  }, [fetchedHelpDeskData]);

  const showLocationModal = (index: number) => setSelectedIndex(index);

  const closeLocationModal = () => setSelectedIndex(undefined);

  const {
    createProductDistribution,
    buttonLoading,
    draftButtonLoading,
    saveAsDraftCreateProductDistribution,
    saveAsDraftUpdateProductDistribution,
    updateProductDistribution,
  } = ProductsService();
  const { fetchSingleProduct: fetchChildProductData, newProduct } =
    ProductCreationService();
  const { fetchProductDistributionListing, productDistributionList } =
    Stage1AndStage2ProductService();

  const [cancelModalState, setCancelModalState] = useState<boolean>(false);
  const isDisabled = isProductDisabled(newProduct?.status);

  /** Fetch single new produt to get the period units from the initial stage */
  useEffect(() => {
    fetchChildProductData(productId);
    fetchProductDistributionListing(productId);
  }, []);

  const handleOpenCancelModal = () => setCancelModalState(true);

  const handleConfirmCancelModal = () =>
    navigate(`${AppRoutes.PRODUCT}/${AppRoutes.DRAFT}`);

  const handleCloseModal = () => setCancelModalState(false);

  const handleTopBackButton = () => navigate(-1);

  const handleGoBack = () =>
    navigate(
      generatePath(
        `${AppRoutes.CREATE_PRODUCT}/${AppRoutes.CREATE_PRODUCT_PROCESSING_FACILITY}`,
        { id: productId }
      )
    );

  const handleSaveAsDraftSubmission = async (
    values: ProductDistributionFormData
  ) => {
    productDistributionList &&
    productDistributionList?.productDistributionRoute?.length
      ? saveAsDraftUpdateProductDistribution(
          productId,
          {
            productDistributionRoute: values?.productDistributionRoute?.length
              ? values?.productDistributionRoute
              : [],
            productDistribution: {
              processingFacilityTotal: Number(values.processingFacilityTotal),
              totalUnit: values.totalUnit ? Number(values.totalUnit) : 0,
              onlineRetail: values.onlineRetail
                ? Number(values.onlineRetail)
                : 0,
            },
          },
          { save_draft: true }
        )
      : saveAsDraftCreateProductDistribution(
          productId,
          {
            productDistributionRoute: values?.productDistributionRoute,
            productDistribution: {
              processingFacilityTotal: Number(values.processingFacilityTotal),
              totalUnit: values.totalUnit ? Number(values.totalUnit) : 0,
              onlineRetail: values.onlineRetail
                ? Number(values.onlineRetail)
                : 0,
            },
          },
          { save_draft: true }
        );
  };

  const handleSubmission = async (values: ProductDistributionFormData) => {
    if (isDisabled === true) {
      navigate(generatePath(AppRoutes.PRODUCT_OVERVIEW, { id }), {
        state: { inCreationPhase: true },
      });
    } else {
      productDistributionList &&
      productDistributionList?.productDistributionRoute?.length
        ? await updateProductDistribution(productId, {
            productDistributionRoute: values?.productDistributionRoute,
            productDistribution: {
              processingFacilityTotal: Number(values.processingFacilityTotal),
              totalUnit: values.totalUnit ? Number(values.totalUnit) : 0,
              onlineRetail: values.onlineRetail
                ? Number(values.onlineRetail)
                : 0,
            },
          })
        : await createProductDistribution(productId, {
            productDistributionRoute: values.productDistributionRoute?.length
              ? values.productDistributionRoute
              : [],
            productDistribution: {
              processingFacilityTotal: Number(values.processingFacilityTotal),
              totalUnit: values.totalUnit ? Number(values.totalUnit) : 0,
              onlineRetail: values.onlineRetail
                ? Number(values.onlineRetail)
                : 0,
            },
          });
    }
  };

  const formInititalValues = {
    productDistributionRoute:
      productDistributionList &&
      productDistributionList.productDistributionRoute?.length
        ? productDistributionList.productDistributionRoute
        : [
            {
              endMarketLocation: '',
              units: undefined,
              transportationAir: false,
              address: undefined,
            },
          ],
    totalPeriodUnits: newProduct && newProduct.reportingPeriodUnits,
    processingFacilityTotal:
      productDistributionList &&
      productDistributionList?.productDistribution?.processingFacilityTotal,
    totalUnit:
      productDistributionList &&
      productDistributionList?.productDistribution?.totalUnit,
    onlineRetail:
      productDistributionList &&
      productDistributionList?.productDistribution?.onlineRetail,
  };

  const onLocationSave = (
    data: Address,
    formikProps: FormikProps<ProductDistributionFormData>
  ) => {
    let wholeAddress = '';
    const { street, city, province, country, postalCode } = data;
    if (street) {
      wholeAddress += street + ', ';
    }
    if (city) {
      wholeAddress += city + ', ';
    }
    if (province) {
      wholeAddress += province + ', ';
    }
    if (country) {
      wholeAddress += country;
    }
    formikProps.setFieldValue(
      `${ProductDistributionProps.PRODUCT_DISTRIBUTION_ROUTES}[${selectedIndex}].${ProcessingFacilityFieldEnums.END_MARKET_LOCATION_NAME}`,
      wholeAddress
    );
    formikProps.setFieldValue(
      `${ProductDistributionProps.PRODUCT_DISTRIBUTION_ROUTES}[${selectedIndex}].${ProcessingFacilityFieldEnums.ADDRESS}`,
      deserialize(Address, data)
    );
    closeLocationModal();
  };

  return (
    <FlowContainer bgColor={DashboardContainerColorProps.GREY}>
      {({}) => (
        <CreateProductContainer>
          <Formik
            enableReinitialize
            validationSchema={validationSchema}
            initialValues={formInititalValues}
            onSubmit={handleSubmission}>
            {formikProps => (
              <form onSubmit={formikProps.handleSubmit}>
                <COAlertModal
                  handleModalCancel={() =>
                    validateForHandleSave(
                      formikProps,
                      handleSaveAsDraftSubmission
                    )
                  }
                  handleModalConfirm={handleConfirmCancelModal}
                  onModalCancel={handleCloseModal}
                  showModalState={cancelModalState}
                  alertModal
                  confirmLabel="Close anyway"
                  cancelLabel="Save & Proceed"
                  heading={
                    'Do you want to close the current page without saving?'
                  }
                />
                {selectedIndex !== undefined && (
                  <FindLocationModal
                    showModal={selectedIndex !== undefined}
                    onSave={(data: Address) =>
                      onLocationSave(data, formikProps)
                    }
                    initialData={
                      formikProps.values.productDistributionRoute?.[
                        selectedIndex ?? -1
                      ]?.address
                    }
                    onCancel={closeLocationModal}
                  />
                )}
                {formikProps && (
                  <ProductDistributionForm
                    formikProps={formikProps}
                    isDisabled={isDisabled}
                    buttonLoading={buttonLoading}
                    draftButtonLoading={draftButtonLoading}
                    handleGoBack={handleGoBack}
                    handleTopBackButton={handleTopBackButton}
                    handleOpenCancelModal={handleOpenCancelModal}
                    validateForHandleSave={validateForHandleSave}
                    handleSaveAsDraftSubmission={handleSaveAsDraftSubmission}
                    showLocationModal={showLocationModal}
                    fetchData={fetchData}
                  />
                )}
              </form>
            )}
          </Formik>
        </CreateProductContainer>
      )}
    </FlowContainer>
  );
};

export default ProductDistribution;
